import React, { useCallback, useMemo } from 'react';
import {
    ReactFlow,
    Background,
    Controls,
    MiniMap,
    useNodesState,
    useEdgesState,
    addEdge,
    Panel
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';

import { nodeTypes } from './nodes';
import { edgeTypes } from './edges';

/**
 * Connection Rules - What can connect to what
 *
 * Campaign → Ad Set (campaigns contain ad sets)
 * Campaign → Segment (campaigns feed segments for targeting)
 * Campaign → Retargeting Pool (campaigns feed pools for retargeting)
 * Campaign → Campaign (retargeting between funnel stages)
 * Ad Set → Ad (ad sets contain ads)
 * Ad Set → A/B Test (ad sets can have A/B tests)
 * A/B Test → Ad (A/B tests contain ad variants)
 * Segment → Ad Set (segments target ad sets)
 * Retargeting Pool → Segment (pools feed segments)
 * Retargeting Pool → Ad Set (pools target ad sets)
 *
 * NOT Allowed:
 * - Campaign → A/B Test directly (A/B tests must be under an Ad Set)
 * - Campaign → Ad directly (Ads must be under an Ad Set)
 * - Ad → anything (Ads are leaf nodes)
 * - A/B Test → Ad Set (wrong direction)
 */
const VALID_CONNECTIONS = {
    campaign: ['adset', 'campaign', 'segment', 'retargetingPool'], // Campaign → Ad Set, retargeting to Campaign, feeds Segment, or feeds RetargetingPool
    adset: ['ad', 'abtest'],                     // Ad Set → Ad or A/B Test
    abtest: ['ad'],                              // A/B Test → Ad (variants)
    ad: [],                                      // Ads are leaf nodes
    segment: ['adset', 'campaign'],              // Segment → targets Ad Sets or Campaigns
    retargetingPool: ['segment', 'adset']        // Retargeting Pool → feeds Segments or Ad Sets
};

// Default viewport - zoomed out to see the full funnel
const DEFAULT_VIEWPORT = { x: 50, y: 50, zoom: 0.65 };

// Funnel stage definitions
const FUNNEL_STAGES = [
    { id: 'TOFU', label: 'Awareness', color: 'var(--color-info)' },
    { id: 'MOFU', label: 'Consideration', color: 'var(--color-warning)' },
    { id: 'BOFU', label: 'Conversion', color: 'var(--color-success)' }
];

// Stage column widths and positions
const STAGE_WIDTH = 350;
const STAGE_GAP = 50;
const STAGE_START_X = 50;

/**
 * FunnelCanvas - Main React Flow canvas for building ad funnels
 *
 * @param {Object} props
 * @param {Array} props.nodes - React Flow nodes
 * @param {Array} props.edges - React Flow edges
 * @param {Function} props.onNodesChange - Node change handler
 * @param {Function} props.onEdgesChange - Edge change handler
 * @param {Function} props.onConnect - Connection handler
 * @param {Function} props.onNodeClick - Node click handler
 * @param {Function} props.onNodeDoubleClick - Node double-click handler (edit)
 * @param {Function} props.onDrop - Drop handler for new nodes
 * @param {Function} props.onDragOver - Drag over handler
 * @param {Object} props.viewport - Current viewport state
 * @param {Function} props.onViewportChange - Viewport change handler
 */
const FunnelCanvas = ({
    nodes = [],
    edges = [],
    onNodesChange,
    onEdgesChange,
    onConnect,
    onNodeClick,
    onPaneClick,
    onNodeDoubleClick,
    onDrop,
    onDragOver,
    onInit,
    defaultViewport = DEFAULT_VIEWPORT,
    isLoading = false
}) => {
    // Filter out invalid edges before rendering to prevent React Flow errors
    const validEdges = useMemo(() => {
        const nodeIds = new Set(nodes.map(n => n.id));

        return edges.filter(edge => {
            // Both source and target must exist
            if (!nodeIds.has(edge.source) || !nodeIds.has(edge.target)) {
                return false;
            }

            // Find source and target nodes
            const sourceNode = nodes.find(n => n.id === edge.source);
            const targetNode = nodes.find(n => n.id === edge.target);

            if (!sourceNode || !targetNode) return false;

            // Check if this connection type is allowed
            const allowedTargets = VALID_CONNECTIONS[sourceNode.type] || [];
            return allowedTargets.includes(targetNode.type);
        });
    }, [nodes, edges]);

    // Validate if a connection is allowed based on node types
    const isValidConnection = useCallback((connection) => {
        const sourceNode = nodes.find(n => n.id === connection.source);
        const targetNode = nodes.find(n => n.id === connection.target);

        if (!sourceNode || !targetNode) return false;

        const sourceType = sourceNode.type;
        const targetType = targetNode.type;

        // Check if this connection type is allowed
        const allowedTargets = VALID_CONNECTIONS[sourceType] || [];
        const isValid = allowedTargets.includes(targetType);

        if (!isValid) {
            console.log(`Invalid connection: ${sourceType} cannot connect to ${targetType}`);
        }

        return isValid;
    }, [nodes]);

    // Connection handler with validation
    const handleConnect = useCallback((params) => {
        if (!isValidConnection(params)) {
            return; // Silently reject invalid connections
        }
        if (onConnect) {
            onConnect(params);
        }
    }, [onConnect, isValidConnection]);

    // Handle node drag stop to snap to funnel stages
    const handleNodeDragStop = useCallback((event, node) => {
        // Calculate which stage the node is closest to
        const nodeX = node.position.x;
        let closestStage = 'TOFU';
        let minDistance = Infinity;

        FUNNEL_STAGES.forEach((stage, index) => {
            const stageCenter = STAGE_START_X + (index * (STAGE_WIDTH + STAGE_GAP)) + (STAGE_WIDTH / 2);
            const distance = Math.abs(nodeX - stageCenter);
            if (distance < minDistance) {
                minDistance = distance;
                closestStage = stage.id;
            }
        });

        // Update node's funnel stage if it changed
        if (node.data.funnelStage !== closestStage) {
            // This will be handled by the parent context
            console.log(`Node ${node.id} moved to stage: ${closestStage}`);
        }
    }, []);

    // Edge styling based on type
    const defaultEdgeOptions = useMemo(() => ({
        style: { strokeWidth: 2 },
        animated: false
    }), []);

    // Custom edge types - imported from ./edges
    const customEdgeTypes = useMemo(() => edgeTypes, []);

    // Render funnel stage backgrounds
    const renderStageBackgrounds = () => (
        <Panel position="top-left" className="funnel-stages-panel">
            <div style={{
                display: 'flex',
                gap: `${STAGE_GAP}px`,
                padding: 'var(--space-4)',
                pointerEvents: 'none',
                position: 'absolute',
                top: 0,
                left: 0,
                height: '100%',
                width: '100%'
            }}>
                {FUNNEL_STAGES.map((stage, index) => (
                    <div
                        key={stage.id}
                        style={{
                            width: `${STAGE_WIDTH}px`,
                            minHeight: '100%',
                            background: `linear-gradient(180deg, ${stage.color}10 0%, transparent 100%)`,
                            borderRadius: 'var(--radius-lg)',
                            border: `2px dashed ${stage.color}30`,
                            padding: 'var(--space-4)',
                            marginLeft: index === 0 ? `${STAGE_START_X}px` : 0
                        }}
                    >
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 'var(--space-2)',
                            marginBottom: 'var(--space-4)'
                        }}>
                            <div style={{
                                width: '12px',
                                height: '12px',
                                borderRadius: '50%',
                                background: stage.color
                            }} />
                            <span style={{
                                fontSize: 'var(--font-size-sm)',
                                fontWeight: 600,
                                color: 'var(--color-text-secondary)',
                                textTransform: 'uppercase',
                                letterSpacing: '0.05em'
                            }}>
                                {stage.id}
                            </span>
                            <span style={{
                                fontSize: 'var(--font-size-xs)',
                                color: 'var(--color-text-tertiary)'
                            }}>
                                ({stage.label})
                            </span>
                        </div>
                    </div>
                ))}
            </div>
        </Panel>
    );

    return (
        <div style={{
            width: '100%',
            height: '100%',
            background: 'var(--color-bg-secondary)',
            borderRadius: 'var(--radius-lg)',
            overflow: 'hidden',
            position: 'relative'
        }}>
            {isLoading && (
                <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(0,0,0,0.3)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 100
                }}>
                    <div style={{
                        padding: 'var(--space-4)',
                        background: 'var(--color-bg-primary)',
                        borderRadius: 'var(--radius-md)',
                        color: 'var(--color-text-primary)'
                    }}>
                        Loading funnel...
                    </div>
                </div>
            )}

            <ReactFlow
                nodes={nodes}
                edges={validEdges}
                onNodesChange={onNodesChange}
                onEdgesChange={onEdgesChange}
                onConnect={handleConnect}
                onNodeClick={onNodeClick}
                onPaneClick={onPaneClick}
                onNodeDoubleClick={onNodeDoubleClick}
                onNodeDragStop={handleNodeDragStop}
                onDrop={onDrop}
                onDragOver={onDragOver}
                onInit={onInit}
                nodeTypes={nodeTypes}
                edgeTypes={customEdgeTypes}
                defaultEdgeOptions={defaultEdgeOptions}
                defaultViewport={defaultViewport}
                isValidConnection={isValidConnection}
                snapToGrid
                snapGrid={[20, 20]}
                connectionLineStyle={{ stroke: 'var(--color-primary)', strokeWidth: 2 }}
                connectionLineType="smoothstep"
                deleteKeyCode={['Backspace', 'Delete']}
                multiSelectionKeyCode={['Shift']}
                selectionKeyCode={['Meta', 'Control']}
                panOnScroll
                zoomOnScroll
                minZoom={0.25}
                maxZoom={1.5}
            >
                <Background
                    color="var(--color-border-light)"
                    gap={20}
                    size={1}
                />
                <Controls
                    style={{
                        button: {
                            background: 'var(--color-bg-primary)',
                            border: '1px solid var(--color-border)',
                            color: 'var(--color-text-primary)'
                        }
                    }}
                />
                <MiniMap
                    style={{
                        background: 'var(--color-bg-tertiary)',
                        border: '1px solid var(--color-border)'
                    }}
                    nodeColor={(node) => {
                        switch (node.type) {
                            case 'campaign':
                                return 'var(--color-primary)';
                            case 'adset':
                                return 'var(--color-info)';
                            case 'ad':
                                return 'var(--color-success)';
                            case 'abtest':
                                return 'var(--color-warning)';
                            case 'segment':
                                return 'var(--color-accent, #9333ea)';
                            case 'retargetingPool':
                                return 'var(--color-warning)';
                            default:
                                return 'var(--color-text-tertiary)';
                        }
                    }}
                    maskColor="rgba(0,0,0,0.2)"
                />

                {/* Stage column headers */}
                <Panel position="top-center">
                    <div style={{
                        display: 'flex',
                        gap: 'var(--space-8)',
                        padding: 'var(--space-3) var(--space-6)',
                        background: 'var(--color-bg-primary)',
                        borderRadius: 'var(--radius-lg)',
                        border: '1px solid var(--color-border)',
                        boxShadow: 'var(--shadow-md)'
                    }}>
                        {FUNNEL_STAGES.map((stage, index) => (
                            <React.Fragment key={stage.id}>
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 'var(--space-2)'
                                }}>
                                    <div style={{
                                        width: '10px',
                                        height: '10px',
                                        borderRadius: '50%',
                                        background: stage.color
                                    }} />
                                    <span style={{
                                        fontSize: 'var(--font-size-sm)',
                                        fontWeight: 600,
                                        color: 'var(--color-text-primary)'
                                    }}>
                                        {stage.id}
                                    </span>
                                    <span style={{
                                        fontSize: 'var(--font-size-xs)',
                                        color: 'var(--color-text-tertiary)'
                                    }}>
                                        {stage.label}
                                    </span>
                                </div>
                                {index < FUNNEL_STAGES.length - 1 && (
                                    <span style={{
                                        color: 'var(--color-text-tertiary)',
                                        fontSize: 'var(--font-size-lg)'
                                    }}>
                                        →
                                    </span>
                                )}
                            </React.Fragment>
                        ))}
                    </div>
                </Panel>

                {/* Help panel - Building hierarchy guide */}
                <Panel position="bottom-center">
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--space-4)',
                        padding: 'var(--space-2) var(--space-4)',
                        background: 'var(--color-bg-primary)',
                        borderRadius: 'var(--radius-lg)',
                        border: '1px solid var(--color-border)',
                        boxShadow: 'var(--shadow-md)',
                        fontSize: 'var(--font-size-xs)',
                        color: 'var(--color-text-secondary)'
                    }}>
                        <span style={{ fontWeight: 600, color: 'var(--color-text-primary)' }}>
                            Build Hierarchy:
                        </span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                            <span style={{
                                padding: '2px 8px',
                                background: 'var(--color-primary)',
                                color: 'white',
                                borderRadius: 'var(--radius-sm)',
                                fontWeight: 500
                            }}>
                                Campaign
                            </span>
                            <span>→</span>
                            <span style={{
                                padding: '2px 8px',
                                background: 'var(--color-info)',
                                color: 'white',
                                borderRadius: 'var(--radius-sm)',
                                fontWeight: 500
                            }}>
                                Ad Set
                            </span>
                            <span>→</span>
                            <span style={{
                                padding: '2px 8px',
                                background: 'var(--color-success)',
                                color: 'white',
                                borderRadius: 'var(--radius-sm)',
                                fontWeight: 500
                            }}>
                                Ad
                            </span>
                        </div>
                        <span style={{ color: 'var(--color-text-tertiary)' }}>|</span>
                        <span>
                            A/B Tests go under Ad Sets
                        </span>
                        <span style={{ color: 'var(--color-text-tertiary)' }}>|</span>
                        <span>
                            Click to edit in panel
                        </span>
                    </div>
                </Panel>
            </ReactFlow>

            <style>{`
                .react-flow__node {
                    cursor: pointer;
                }
                .react-flow__node.selected {
                    outline: none;
                }
                .react-flow__handle {
                    width: 10px;
                    height: 10px;
                    background: var(--color-primary);
                    border: 2px solid var(--color-bg-primary);
                }
                .react-flow__handle:hover {
                    background: var(--color-primary-dark);
                }
                .react-flow__edge-path {
                    stroke: var(--color-border);
                }
                .react-flow__edge.selected .react-flow__edge-path {
                    stroke: var(--color-primary);
                }
                .react-flow__controls {
                    box-shadow: var(--shadow-md);
                    border-radius: var(--radius-md);
                    overflow: hidden;
                }
                .react-flow__controls-button {
                    background: var(--color-bg-primary) !important;
                    border-bottom: 1px solid var(--color-border) !important;
                    color: var(--color-text-primary) !important;
                }
                .react-flow__controls-button:hover {
                    background: var(--color-bg-secondary) !important;
                }
                .react-flow__controls-button svg {
                    fill: currentColor !important;
                }
                @keyframes dashdraw {
                    from {
                        stroke-dashoffset: 10;
                    }
                    to {
                        stroke-dashoffset: 0;
                    }
                }
            `}</style>
        </div>
    );
};

export default FunnelCanvas;
